import React, {useCallback, useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import Translate from "../../components/Translate";
import {hotkeyKeys} from "../../utils/hotkeys";
import './Tooltip.css';

const GAP = 8;              // distance between the anchor and the bubble
const VIEWPORT_MARGIN = 8;  // keep the bubble this far from the window edges
const SHOW_DELAY = 120;     // don't flash while the pointer just passes over
const HIDE_DURATION = 140;  // must match the CSS transition

const Tooltip = ({
                     text,
                     children,
                     prefix = '',
                     /* the combination that does the same thing, if the operator has one */
                     shortcut = '',
                     needTranslate = true,
                     borderRadius = '8px',
                     background = '#242424',
                     textColor = '#ffffff',
                     inRightSide = false,
                     inLeftSide = false,
                     width,
                     height,
                     deltaY = 0,
                     delay = SHOW_DELAY,
                     disabled = false,
                     asChild = false,
                     style = {}
                 }) => {
    const [mounted, setMounted] = useState(false);
    const [visible, setVisible] = useState(false);
    const [placement, setPlacement] = useState('top');
    const [coords, setCoords] = useState({top: 0, left: 0});

    const anchorRef = useRef(null);
    const bubbleRef = useRef(null);
    const showTimer = useRef(null);
    const hideTimer = useRef(null);

    const clearTimers = () => {
        clearTimeout(showTimer.current);
        clearTimeout(hideTimer.current);
    };

    const hide = useCallback(() => {
        clearTimers();
        setVisible(false);
        hideTimer.current = setTimeout(() => setMounted(false), HIDE_DURATION);
    }, []);

    useEffect(() => {
        const handleGlobalClick = () => hide();
        const handleScroll = () => hide();
        const handleBlur = () => hide();

        window.addEventListener('click', handleGlobalClick, true);
        window.addEventListener('scroll', handleScroll, true);
        window.addEventListener('blur', handleBlur);

        return () => {
            window.removeEventListener('click', handleGlobalClick, true);
            window.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('blur', handleBlur);
            clearTimers();
        };
    }, [hide]);

    const basePlacement = inRightSide ? 'right' : inLeftSide ? 'left' : 'top';

    const positionFor = (rect, side) => {
        if (side === 'right') return {top: rect.top + rect.height / 2, left: rect.right + GAP};
        if (side === 'left') return {top: rect.top + rect.height / 2, left: rect.left - GAP};
        if (side === 'bottom') return {top: rect.bottom + GAP, left: rect.left + rect.width / 2};
        return {top: rect.top - GAP, left: rect.left + rect.width / 2};
    };

    const handleMouseEnter = () => {
        // switched off rather than unmounted: a caller that shows the tooltip
        // only some of the time would otherwise have to swap this wrapper in and
        // out, which replaces everything inside it and kills any transition the
        // children were in the middle of
        if (disabled || !anchorRef.current) return;

        clearTimers();
        showTimer.current = setTimeout(() => {
            const rect = anchorRef.current.getBoundingClientRect();

            setPlacement(basePlacement);
            setCoords(positionFor(rect, basePlacement));
            setMounted(true);
        }, delay);
    };

    /**
     * Once the bubble is in the DOM we know its size, so a placement that would
     * run off-screen can be flipped and the position nudged back inside. Runs
     * before paint, so the corrected spot is the first thing drawn.
     */
    useEffect(() => {
        if (!mounted || !anchorRef.current || !bubbleRef.current) return;

        const anchor = anchorRef.current.getBoundingClientRect();
        const bubble = bubbleRef.current.getBoundingClientRect();

        let side = basePlacement;

        if (side === 'top' && anchor.top - bubble.height - GAP < VIEWPORT_MARGIN) {
            side = 'bottom';
        } else if (side === 'right' && anchor.right + bubble.width + GAP > window.innerWidth - VIEWPORT_MARGIN) {
            side = 'left';
        } else if (side === 'left' && anchor.left - bubble.width - GAP < VIEWPORT_MARGIN) {
            side = 'right';
        }

        const next = positionFor(anchor, side);

        // horizontal clamp for the centred placements
        if (side === 'top' || side === 'bottom') {
            const half = bubble.width / 2;
            const min = VIEWPORT_MARGIN + half;
            const max = window.innerWidth - VIEWPORT_MARGIN - half;
            next.left = Math.min(Math.max(next.left, min), max);
        }

        setPlacement(side);
        setCoords(next);
        setVisible(true);
    }, [mounted, basePlacement]);

    const bubbleStyle = {
        top: coords.top + deltaY,
        left: coords.left,
        backgroundColor: background,
        color: textColor,
        borderRadius,
    };

    const classes = [
        'tooltip-bubble',
        'tooltip-bubble--' + placement,
        visible ? 'tooltip-bubble--visible' : ''
    ].filter(Boolean).join(' ');

    const anchor = asChild
        ? React.cloneElement(React.Children.only(children), {
            ref: anchorRef,
            onMouseEnter: (event) => {
                if (children.props.onMouseEnter) children.props.onMouseEnter(event);
                handleMouseEnter();
            },
            onMouseLeave: (event) => {
                if (children.props.onMouseLeave) children.props.onMouseLeave(event);
                hide();
            },
        })
        : (
            <div
                ref={anchorRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={hide}
                style={{
                    ...(width ? {width} : {}),
                    ...(height ? {height} : {}),
                    ...style
                }}
            >
                {children}
            </div>
        );

    return (
        <React.Fragment>
            {anchor}

            {mounted && text &&
                createPortal(
                    <div ref={bubbleRef} className={classes} style={bubbleStyle} role="tooltip">
                        <span className="tooltip-bubble__arrow"/>
                        {needTranslate
                            ? <React.Fragment>{prefix} <Translate text={text}/></React.Fragment>
                            : <React.Fragment>{prefix} {text}</React.Fragment>
                        }
                        {shortcut && (
                            <span className="tooltip-bubble__keys">
                                {hotkeyKeys(shortcut).map(key => (
                                    <kbd key={key} className="tooltip-bubble__key">{key}</kbd>
                                ))}
                            </span>
                        )}
                    </div>,
                    document.body
                )
            }
        </React.Fragment>
    );
};

export default Tooltip;



// WEBPACK FOOTER //
// ./src/components-new/general/Tooltip.jsx